/**
 * Sprint Documents Tab Content Component
 *
 * Displays all markdown and JSON documents for a sprint organized by stage folder.
 * Supports markdown rendering with syntax highlighting and JSON pretty-printing.
 *
 * Module: Sprint Management (SPRINT-2 Enhancement - Phase 3)
 * PRD: docs/sprints/SPRINT-2/sprint-module-enhancement-handoff.json
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FileText, FileJson, ChevronDown, ChevronRight, Eye, FolderOpen, FileWarning } from "lucide-react";
import { format } from "date-fns";

interface Document {
  filename: string;
  path: string;
  type: "markdown" | "json";
  size: number;
  modified: string;
  category: string;
}

interface DocumentsData {
  sprintNumber: string;
  totalDocuments: number;
  documents: Document[];
  groupedByCategory: Record<string, Document[]>;
}

interface DocumentContent {
  filename: string;
  path: string;
  type: "markdown" | "json";
  size: number;
  modified: string;
  content: string;
}

interface DocumentsTabContentProps {
  sprintNumber: string;
}

export function DocumentsTabContent({ sprintNumber }: DocumentsTabContentProps) {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});

  // Fetch documents list
  const { data: documentsData, isLoading } = useQuery<DocumentsData>({
    queryKey: ["/api/project-manager/sprints/by-number", sprintNumber, "documents"],
    enabled: !!sprintNumber,
  });

  // Fetch specific document content
  const { data: documentContent, isLoading: isLoadingContent } = useQuery<DocumentContent>({
    queryKey: ["/api/project-manager/sprints/by-number", sprintNumber, "documents", selectedDocument?.path],
    enabled: !!selectedDocument,
  });

  const toggleFolder = (folderName: string) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  const handleViewDocument = (doc: Document) => {
    setSelectedDocument(doc);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDocument(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const getFolderDisplayName = (folderName: string): string => {
    // Convert "01-PLANNING" to "01-Planning" or "ARCHIVE" to "Archive"
    const match = folderName.match(/^(\d{2})-(.+)$/);
    if (match) {
      const number = match[1];
      const name = match[2]
        .split("-")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" ");
      return `${number}-${name}`;
    }
    // For folders without numbers (ARCHIVE, SESSION-SUMMARIES, etc.)
    return folderName
      .split("-")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading documents...</p>
      </div>
    );
  }

  if (!documentsData || documentsData.totalDocuments === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FileWarning className="h-16 w-16 text-muted-foreground mb-4" />
        <p className="text-lg font-medium mb-2">No documents found</p>
        <p className="text-sm text-muted-foreground mb-6">
          Sprint documentation will appear here once available.
        </p>
      </div>
    );
  }

  // Get sorted folders
  const folders = Object.keys(documentsData.groupedByCategory).sort();

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            Sprint Documents ({documentsData.totalDocuments})
          </h3>
          <Badge variant="outline">
            <FolderOpen className="h-3 w-3 mr-1" />
            {folders.length} Folders
          </Badge>
        </div>

        {/* Folder Tree View */}
        <div className="space-y-2">
          {folders.map((folderName) => {
            const docs = documentsData.groupedByCategory[folderName];
            const isOpen = openFolders[folderName] ?? false; // Default collapsed

            return (
              <Card key={folderName}>
                <CardContent className="p-0">
                  <Collapsible open={isOpen} onOpenChange={() => toggleFolder(folderName)}>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-start px-4 py-3 hover:bg-muted/50"
                      >
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 mr-2" />
                        )}
                        <FolderOpen className="h-4 w-4 mr-2 text-blue-500" />
                        <span className="font-medium">{getFolderDisplayName(folderName)}</span>
                        <Badge variant="secondary" className="ml-auto">
                          {docs.length}
                        </Badge>
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-3">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30">
                              <TableHead className="w-[40px]">Type</TableHead>
                              <TableHead>Filename</TableHead>
                              <TableHead className="w-[100px]">Size</TableHead>
                              <TableHead className="w-[140px]">Modified</TableHead>
                              <TableHead className="w-[80px] text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {docs.map((doc) => {
                              const Icon = doc.type === "markdown" ? FileText : FileJson;
                              const iconColor = doc.type === "markdown" ? "text-blue-600" : "text-green-600";

                              return (
                                <TableRow key={doc.path} className="hover:bg-muted/30">
                                  <TableCell>
                                    <Icon className={`h-4 w-4 ${iconColor}`} />
                                  </TableCell>
                                  <TableCell>
                                    <button
                                      onClick={() => handleViewDocument(doc)}
                                      className="text-sm font-medium hover:underline text-left"
                                      title={doc.path}
                                    >
                                      {doc.filename}
                                    </button>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-xs text-muted-foreground">
                                      {formatFileSize(doc.size)}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(doc.modified), "MMM d, yyyy")}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleViewDocument(doc)}
                                      title="View document"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" aria-describedby="document-viewer-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDocument?.type === "markdown" ? (
                <FileText className="h-5 w-5 text-blue-600" />
              ) : (
                <FileJson className="h-5 w-5 text-green-600" />
              )}
              {selectedDocument?.filename}
            </DialogTitle>
            <DialogDescription id="document-viewer-description">
              {selectedDocument?.path} • {selectedDocument && formatFileSize(selectedDocument.size)}
            </DialogDescription>
          </DialogHeader>

          {isLoadingContent ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-sm text-muted-foreground">Loading document...</p>
            </div>
          ) : documentContent ? (
            <div className="mt-4">
              {documentContent.type === "markdown" ? (
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {documentContent.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <pre className="bg-muted p-4 rounded-md overflow-x-auto text-xs">
                  <code>{JSON.stringify(JSON.parse(documentContent.content), null, 2)}</code>
                </pre>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">Failed to load document</p>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={handleCloseViewer}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
